import React, {
	useCallback, useEffect, useMemo, useState,
} from 'react';
import { takeUntil, tap } from 'rxjs/operators';
import { merge, Subject } from 'rxjs';

import useStyles from 'isomorphic-style-loader/useStyles';
import { Container } from '../../../../symphony';

import { ActionType, RequestType } from '../../global-interface';
import { SlDataService } from '../../services/sl-data.service';
import { SlActions } from '../../services/ActionsService/sl-actions';
import CodeLoader from '../../components/Loader/CodeLoader/CodeLoader';
import { EditorWithLabel } from '../../components/EditorWithLabel/EditorWithLabel';
import { OutputIframeContainer } from '../../components/Output/OutputIframeContainer';
import { OutputConsole } from '../../components/Output/OutputConsole/OutputConsole';

import s from './Web.scss';
import { Orientation } from '../../../../shared/public/SlCodeEditorComponents/types/types';
import { ICollapsedPanels, SplitPane } from '../../../../shared/public/SlCodeEditorComponents/SplitPane/SplitPane';
import { Panel } from '../../../../shared/public/SlCodeEditorComponents/Panel/Panel';
import { OverlapLayer } from '../../../../shared/public/SlCodeEditorComponents/OverlapLayer/OverlapLayer';
import { CollapsedView } from '../../../../shared/public/SlCodeEditorComponents/CollapsedView/CollapsedView';
import { CodeLanguages } from '../../../../api/private/global.interface';
import { OutputHeader } from '../../../../shared/public/SlCodeEditorComponents/OutputHeader/OutputHeader';
import { useContainerData } from '../../../../shared/public/SlHooks/useContainerData';
import { SlEventsService } from '../../services/Business/sl-events.service';
import { lePlaygroundContext } from '../../global-constants';

export function Web(): JSX.Element {
	useStyles(s);
	const [collapsedPanels, setCollapsedPanels] = useState<ICollapsedPanels>(
		{},
	);
	const [collapsesStates, setCollapsesStates] = useState({});

	const eventService = Container.take(lePlaygroundContext.value, SlEventsService);

	const {
		sourceCode$: sourceCode, cssCode$: cssCode, jsCode$: jsCode, output$: output, isDarkMode$: isDarkMode, isResizingPanel$: isResizingPanel, expandedTIYEditorRefs, isTIYExpanded, outputConsole$: outputConsole,
	} = useContainerData(lePlaygroundContext.value, SlDataService, ['sourceCode$', 'cssCode$', 'jsCode$', 'output$', 'isDarkMode$', 'showConsole$', 'isResizingPanel$', 'expandedTIYEditorRefs', 'isTIYExpanded', 'outputConsole$']);

	const {
		showConfirmLeaveAlert$,
		isResizingPanel$,
		outputConsole$,
	} = Container.take(lePlaygroundContext.value, SlDataService);

	const { actions$ } = Container.take(lePlaygroundContext.value, SlActions);

	const openConsole = (): void => {
		actions$.next({
			type: ActionType.OpenConsole,
			requestType: RequestType.Parallel,
		});
	};

	useEffect(() => {
		const unsubscriber = new Subject<void>();

		merge(
			outputConsole$.pipe(
				tap((data) => {
					if (data) {
						openConsole();
					}
				}),
			),
		)
			.pipe(takeUntil(unsubscriber))
			.subscribe();

		return () => {
			unsubscriber.next();
			unsubscriber.complete();
		};
	}, []);

	const onWebCodeChange = (value: string, lng: CodeLanguages): void => {
		if (!showConfirmLeaveAlert$.value) {
			showConfirmLeaveAlert$.next(true);
		}
		eventService.onWebCodeChange(value, lng);
	};

	const onMouseDown = (): void => {
		isResizingPanel$.next(true);
	};

	const onMouseUp = (): void => {
		isResizingPanel$.next(false);
	};

	const handleResize = useCallback(
		(sizes) => {
			let collapseStateChange = false;
			let mergeState = collapsesStates;
			const MIN_WIDTH = 48;
			sizes.forEach((size) => {
				if (size.width <= MIN_WIDTH) {
					if (!mergeState[size.id]) {
						collapseStateChange = true;
						mergeState = {
							...collapsesStates,
							[size.id]: true,
						};
					}
				} else if (size.width > MIN_WIDTH) {
					if (mergeState[size.id]) {
						collapseStateChange = true;
						mergeState = {
							...collapsesStates,
							[size.id]: false,
						};
					}
				}
			});
			if (collapseStateChange) {
				setCollapsesStates(mergeState);
			}
		},
		[collapsesStates],
	);

	const onCollapseClick = (id: string, orientation: Orientation) => (): void => {
		setCollapsedPanels({
			state: true,
			currentId: id,
			[id]: true,
			orientation,
		});
		setCollapsesStates({
			...collapsesStates,
			[id]: false,
		});
	};

	const callbackCollapseClicked = (id: string): void => {
		setCollapsedPanels({
			state: false,
			currentId: id,
			[id]: false,
			orientation: 'horizontal',
		});
	};

	const consoleStyleOptions = useMemo((): {
		[key: string]: string;
	} => ({
		height: outputConsole ? '232px' : '56px',
	}), [outputConsole]);

	const getOutput = () => (
		<div className="sl-web-wrapper__frame-wrapper">
			<OutputHeader title="BROWSER" />
			<SplitPane
				callbackCollapseClicked={callbackCollapseClicked}
				defaultWidth={25}
				horizontal={false}
				onMouseUp={onMouseUp}
				onMouseDown={onMouseDown}
				onResize={handleResize}
				collapsedPanels={collapsedPanels}
				className="split-pane__vertical"
			>
				<Panel
					isDarkMode={isDarkMode}
					hideResizer
					style={outputConsole ? {} : { height: '100%' }}
				>
					<div className="sl-web-wrapper__frame-wrapper__iframe">
						<OverlapLayer isResizingPanel={isResizingPanel} />
						{isTIYExpanded && (
							<OutputIframeContainer
								value={output}
								isWeb
							/>
						)}
					</div>
				</Panel>
				<Panel isDarkMode={isDarkMode} style={consoleStyleOptions}>
					<OutputConsole isDarkMode={false} />
				</Panel>
			</SplitPane>
		</div>
	);

	return (
		<SplitPane
			horizontal
			onResize={handleResize}
			className="sl-web-wrapper"
			onMouseUp={onMouseUp}
			onMouseDown={onMouseDown}
			collapsedPanels={collapsedPanels}
			callbackCollapseClicked={callbackCollapseClicked}
			defaultWidth={25}
			isWeb
			components={{
				loader: <CodeLoader />,
			}}
		>
			<Panel width="25%" isDarkMode={isDarkMode}>
				<div className="panel-wrapper">
					<CollapsedView
						show={collapsesStates['0']}
						title="html"
						isDarkMode={isDarkMode}
						onClick={onCollapseClick('0', 'horizontal')}
					/>
					<EditorWithLabel
						codeEditorRef={expandedTIYEditorRefs[0]}
						value={sourceCode}
						language={CodeLanguages.HTML}
						onChange={onWebCodeChange}
						isDarkMode={isDarkMode}
					/>
				</div>
			</Panel>
			<Panel width="25%" isDarkMode={isDarkMode}>
				<div className="panel-wrapper">
					<CollapsedView
						show={collapsesStates['1']}
						isDarkMode={isDarkMode}
						title="css"
						onClick={onCollapseClick('1', 'horizontal')}
					/>
					<EditorWithLabel
						codeEditorRef={expandedTIYEditorRefs[1]}
						value={cssCode}
						language={CodeLanguages.CSS}
						onChange={onWebCodeChange}
						isDarkMode={isDarkMode}
					/>
				</div>
			</Panel>
			<Panel width="25%" isDarkMode={isDarkMode}>
				<div className="panel-wrapper">
					<CollapsedView
						show={collapsesStates['2']}
						isDarkMode={isDarkMode}
						title="js"
						onClick={onCollapseClick('2', 'horizontal')}
					/>
					<EditorWithLabel
						codeEditorRef={expandedTIYEditorRefs[2]}
						value={jsCode}
						language={CodeLanguages.JS}
						onChange={onWebCodeChange}
						isDarkMode={isDarkMode}
					/>
				</div>
			</Panel>
			<Panel width="25%" isDarkMode={isDarkMode}>
				{getOutput()}
			</Panel>
		</SplitPane>
	);
}
